import { useI18n } from 'i18n-utils'
import { DialogHeader } from '../components/DialogHeader'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { useClosePosition } from '../stores/use-close-position'
import { DefineComponentProps } from 'utils'
import { ContractOrderType, OrderDirection } from 'network'

import { CustomButton, HocElSlider, InputView, ListCell, Pnl, Tag, ToggleIcon } from '..'
import { useExchangeInfoStore } from 'store/src/stores/use-exchange-info'
import { useBaseInfoStore, useProjectTheme } from 'store'
import './ClosePosition.scss'


export type ClosePositionProps = {
  position: ApiResponse.DoubleContractPosition
  onClose?: () => void
}

export const ClosePosition = defineComponent<ClosePositionProps>({
  props: DefineComponentProps<ClosePositionProps>(['onClose', 'position']),
  name: 'ClosePosition',
  setup(props) {
    const { t } = useI18n()
    const closePositionStore = useClosePosition(props.position)
    const exchangeStore = useExchangeInfoStore()
    const baseInfoStore = useBaseInfoStore()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)

    onBeforeMount(() => {
      closePositionStore.open()
    })

    const closeName = computed(() => {
      if (props.position.direction === OrderDirection.Buy) {
        return t('平多')
      } else {
        return t('平空')
      }
    })

    const onClose = () => {
      if (!closePositionStore.closePositionVisible.value) return
      closePositionStore.close()
      props.onClose?.()
    }

    const fromSymbolName = computed(() => exchangeStore.getFromSymbolNameBySymbolId(props.position.symbolId))

    const toSymbolName = computed(() => exchangeStore.getToSymbolNameBySymbolId(props.position.symbolId))

    const config = computed(() => {
      if (!props.position.symbolId) return null
      return exchangeStore.getContractSymbolInfoBySymbolId(props.position.symbolId)
    })

    /** 价格精度 */
    const coinToPrecision = computed(() => {
      if (!props.position.symbolId) return 2
      const data = baseInfoStore.getCoinStaticItemDataBySymbolId(props.position.symbolId)
      if (!data) return 2
      return data.pricePrecision
    })

    return () => (
      <HocElDialog width={520} v-model={closePositionStore.closePositionVisible.value} showClose={false} onClose={onClose}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('平仓')}</DialogHeader>,
          default: () => (
            <div class="close-position-layout">
              <div class="coin-info">
                <div class="icon-wrapper">
                  <img src={closePositionStore.getSymbolIcon()} alt="coin" />
                </div>
                <div>
                  <div class="coin-name">
                    {closePositionStore.symbolInfo.value?.symbolName?.toUpperCase()}
                    {t('永续')}
                  </div>
                  <div class="position-info mt-[20px]">
                    <Tag color={props.position.direction === OrderDirection.Sell ? theme.value.colors.up : theme.value.colors.down}>{closeName.value}</Tag>
                    <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                      {props.position.marginType === 1 ? t('逐仓') : t('全仓')}
                      {props.position.leverage}X
                    </Tag>
                  </div>
                </div>
              </div>
              <div class="position-info-list">
                <ListCell title={t('开仓价格')} description={props.position.holdPrice.formatNumberWithCommas()} />
                <ListCell title={t('标记价格')} description={closePositionStore.markPrice.value?.formatNumberWithCommas()} />
              </div>
              <div class="position-form">
                {/* 委托类型 */}
                <div class="position-form-item type">
                  <span class="place-order-type">{t('委托类型')}</span>
                  <span class="place-order-type-value" onClick={closePositionStore.switchOrderType}>
                    <ToggleIcon />
                    {t(closePositionStore.orderType.value === ContractOrderType.Market ? '市价' : '限价')}
                  </span>
                </div>
                <div class="position-form-item">
                  <InputView
                    v-model={closePositionStore.limitPrice.value}
                    radius={6}
                    disabled={closePositionStore.orderType.value === ContractOrderType.Market}
                    placeholder={t(closePositionStore.orderType.value === ContractOrderType.Market ? '市价' : '请输入价格')}
                    isFloatNumber={closePositionStore.orderType.value === ContractOrderType.Limit}
                    maxDecimalLength={coinToPrecision.value}
                    suffixIcon={() => (closePositionStore.orderType.value === ContractOrderType.Limit ? 'USDT' : '')}
                  />
                </div>
                <div class="position-form-item">
                  <InputView
                    v-model={closePositionStore.amount.value}
                    isFloatNumber
                    onInput={(value) => closePositionStore.onAmountChange(+value, props.position.positionAmt)}
                    placeholder={t('数量' + `(${fromSymbolName.value})`)}
                    maxDecimalLength={config.value?.quantityPrecision || 2}
                    max={props.position.positionAmt}
                    suffixIcon={() => fromSymbolName.value}
                  />
                  <div class="position-form-item-amount-info">
                    <span>{t('可平')}</span>
                    <span class="amount-value">{props.position.positionAmt.formatNumberWithCommas()}</span>
                    <span class="amount-unit">{fromSymbolName.value}</span>
                  </div>
                </div>

                <div class="position-form-item">
                  <HocElSlider
                    v-model={closePositionStore.sliderValue.value}
                    marks={closePositionStore.sliderMarks.value}
                    max={100}
                    formatTooltip={(value) => `${value}%`}
                    onInput={(value) => closePositionStore.onSliderInput(value as number)}
                  />
                </div>
                <div class="position-form-item">
                  <div class="estimated-profit">
                    <span class="estimated-profit-title">{t('预计盈亏')}</span>
                    <span class="estimated-profit-value">
                      <Pnl value={closePositionStore.estimatedProfit.value}>{toSymbolName.value}</Pnl>
                    </span>
                  </div>
                </div>
                <div class="close-btn">
                  <CustomButton
                    type={'primary'}
                    loading={closePositionStore.loading.value}
                    primaryColor={props.position.direction === OrderDirection.Buy ? 'var(--colors-down)' : 'var(--colors-up)'}
                    onClick={closePositionStore.onClosePosition}
                  >
                    {closeName.value}
                  </CustomButton>
                </div>
              </div>
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
